import { ArchitectureNode, ArchitectureEdge, ComponentType } from '../types';
import jsYaml from 'js-yaml';

interface KubernetesResource {
  apiVersion: string;
  kind: string;
  metadata: {
    name: string;
    namespace?: string;
    labels?: Record<string, string>;
  };
  spec?: any;
}

const componentToKubernetes = (
  node: ArchitectureNode,
  namespace: string = 'default'
): KubernetesResource[] => {
  const { type, label, specs } = node.data;
  const resourceName = label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  switch (type) {
    case ComponentType.MICROSERVICE:
    case ComponentType.WORKER:
    case ComponentType.WEB_SERVER:
      // Create Deployment and Service
      return [
        {
          apiVersion: 'apps/v1',
          kind: 'Deployment',
          metadata: {
            name: resourceName,
            namespace,
            labels: { app: resourceName }
          },
          spec: {
            replicas: 1,
            selector: { matchLabels: { app: resourceName } },
            template: {
              metadata: { labels: { app: resourceName } },
              spec: {
                containers: [{
                  name: resourceName,
                  image: `${resourceName}:latest`,
                  ports: [{ containerPort: 8080 }],
                  resources: {
                    requests: {
                      cpu: specs?.cpu || '100m',
                      memory: specs?.memory || '128Mi'
                    },
                    limits: {
                      cpu: specs?.cpu || '500m',
                      memory: specs?.memory || '512Mi'
                    }
                  }
                }]
              }
            }
          }
        },
        {
          apiVersion: 'v1',
          kind: 'Service',
          metadata: {
            name: `${resourceName}-service`,
            namespace,
            labels: { app: resourceName }
          },
          spec: {
            selector: { app: resourceName },
            ports: [{
              port: 80,
              targetPort: 8080,
              protocol: 'TCP'
            }],
            type: 'ClusterIP'
          }
        }
      ];

    case ComponentType.DATABASE:
      // Create StatefulSet and Service for database
      return [
        {
          apiVersion: 'apps/v1',
          kind: 'StatefulSet',
          metadata: {
            name: resourceName,
            namespace,
            labels: { app: resourceName }
          },
          spec: {
            serviceName: `${resourceName}-service`,
            replicas: 1,
            selector: { matchLabels: { app: resourceName } },
            template: {
              metadata: { labels: { app: resourceName } },
              spec: {
                containers: [{
                  name: resourceName,
                  image: specs?.engine?.toLowerCase().includes('postgres') 
                    ? 'postgres:15' 
                    : specs?.engine?.toLowerCase().includes('mysql')
                    ? 'mysql:8'
                    : 'postgres:15',
                  ports: [{ containerPort: 5432 }],
                  env: [
                    { name: 'POSTGRES_DB', value: resourceName },
                    { name: 'POSTGRES_USER', value: 'admin' },
                    { name: 'POSTGRES_PASSWORD', value: 'changeme' } // Should use Secret
                  ],
                  volumeMounts: [{
                    name: 'data',
                    mountPath: '/var/lib/postgresql/data'
                  }],
                  resources: {
                    requests: {
                      cpu: specs?.cpu || '200m',
                      memory: specs?.memory || '256Mi'
                    },
                    limits: {
                      cpu: specs?.cpu || '1000m',
                      memory: specs?.memory || '1Gi'
                    }
                  }
                }]
              }
            },
            volumeClaimTemplates: [{
              metadata: { name: 'data' },
              spec: {
                accessModes: ['ReadWriteOnce'],
                resources: { requests: { storage: '10Gi' } }
              }
            }]
          }
        },
        {
          apiVersion: 'v1',
          kind: 'Service',
          metadata: {
            name: `${resourceName}-service`,
            namespace,
            labels: { app: resourceName }
          },
          spec: {
            selector: { app: resourceName },
            ports: [{
              port: 5432,
              targetPort: 5432,
              protocol: 'TCP'
            }],
            type: 'ClusterIP'
          }
        }
      ];

    case ComponentType.CACHE:
      // Redis deployment
      return [
        {
          apiVersion: 'apps/v1',
          kind: 'Deployment',
          metadata: {
            name: resourceName,
            namespace,
            labels: { app: resourceName }
          },
          spec: {
            replicas: 1,
            selector: { matchLabels: { app: resourceName } },
            template: {
              metadata: { labels: { app: resourceName } },
              spec: {
                containers: [{
                  name: resourceName,
                  image: 'redis:7-alpine',
                  ports: [{ containerPort: 6379 }],
                  resources: {
                    requests: {
                      cpu: specs?.cpu || '100m',
                      memory: specs?.memory || '128Mi'
                    },
                    limits: {
                      cpu: specs?.cpu || '500m',
                      memory: specs?.memory || '512Mi'
                    }
                  }
                }]
              }
            }
          }
        },
        {
          apiVersion: 'v1',
          kind: 'Service',
          metadata: {
            name: `${resourceName}-service`,
            namespace,
            labels: { app: resourceName }
          },
          spec: {
            selector: { app: resourceName },
            ports: [{
              port: 6379,
              targetPort: 6379,
              protocol: 'TCP'
            }],
            type: 'ClusterIP'
          }
        }
      ];

    case ComponentType.MESSAGE_BROKER:
      // RabbitMQ or Kafka
      return [
        {
          apiVersion: 'apps/v1',
          kind: 'Deployment',
          metadata: {
            name: resourceName,
            namespace,
            labels: { app: resourceName }
          },
          spec: {
            replicas: 1,
            selector: { matchLabels: { app: resourceName } },
            template: {
              metadata: { labels: { app: resourceName } },
              spec: {
                containers: [{
                  name: resourceName,
                  image: 'rabbitmq:3-management',
                  ports: [
                    { containerPort: 5672 },
                    { containerPort: 15672 }
                  ],
                  resources: {
                    requests: {
                      cpu: specs?.cpu || '200m',
                      memory: specs?.memory || '256Mi'
                    },
                    limits: {
                      cpu: specs?.cpu || '1000m',
                      memory: specs?.memory || '1Gi'
                    }
                  }
                }]
              }
            }
          }
        },
        {
          apiVersion: 'v1',
          kind: 'Service',
          metadata: {
            name: `${resourceName}-service`,
            namespace,
            labels: { app: resourceName }
          },
          spec: {
            selector: { app: resourceName },
            ports: [
              { port: 5672, targetPort: 5672, name: 'amqp' },
              { port: 15672, targetPort: 15672, name: 'management' }
            ],
            type: 'ClusterIP'
          }
        }
      ];

    case ComponentType.LOAD_BALANCER:
      // Create Service with LoadBalancer type
      return [
        {
          apiVersion: 'v1',
          kind: 'Service',
          metadata: {
            name: `${resourceName}-service`,
            namespace,
            labels: { app: resourceName }
          },
          spec: {
            selector: { app: resourceName }, // Should reference actual app
            ports: [{
              port: 80,
              targetPort: 8080,
              protocol: 'TCP'
            }],
            type: 'LoadBalancer'
          }
        }
      ];

    case ComponentType.STORAGE:
      // Create PersistentVolumeClaim
      return [
        {
          apiVersion: 'v1',
          kind: 'PersistentVolumeClaim',
          metadata: {
            name: resourceName,
            namespace
          },
          spec: {
            accessModes: ['ReadWriteOnce'],
            resources: {
              requests: {
                storage: '10Gi'
              }
            }
          }
        }
      ];

    default:
      // Generic Deployment for unknown types
      return [
        {
          apiVersion: 'apps/v1',
          kind: 'Deployment',
          metadata: {
            name: resourceName,
            namespace,
            labels: { app: resourceName, component: type }
          },
          spec: {
            replicas: 1,
            selector: { matchLabels: { app: resourceName } },
            template: {
              metadata: { labels: { app: resourceName } },
              spec: {
                containers: [{
                  name: resourceName,
                  image: 'nginx:latest',
                  ports: [{ containerPort: 80 }]
                }]
              }
            }
          }
        }
      ];
  }
};

export const generateKubernetes = (
  nodes: ArchitectureNode[],
  edges: ArchitectureEdge[],
  namespace: string = 'default',
  format: 'single' | 'separate' = 'single'
): string | Record<string, string> => {
  const allResources: KubernetesResource[] = [];
  
  // Convert each node to Kubernetes resources
  nodes.forEach(node => {
    const resources = componentToKubernetes(node, namespace);
    allResources.push(...resources);
  });

  // Add ConfigMap and Secret placeholders
  allResources.push({
    apiVersion: 'v1',
    kind: 'ConfigMap',
    metadata: {
      name: 'app-config',
      namespace
    },
    data: {
      'config.yaml': '# Add your configuration here'
    }
  });

  allResources.push({
    apiVersion: 'v1',
    kind: 'Secret',
    metadata: {
      name: 'app-secrets',
      namespace
    },
    type: 'Opaque',
    data: {
      // Base64 encoded - should be replaced with actual secrets
      'password': 'Y2hhbmdlbWU=' // "changeme" in base64
    }
  });

  if (format === 'single') {
    // Generate single YAML file with all resources
    return allResources
      .map(resource => {
        return `---\n${jsYaml.dump(resource, { indent: 2 })}`;
      })
      .join('\n');
  } else {
    // Generate separate files per resource type
    const files: Record<string, string> = {};
    const grouped = allResources.reduce((acc, resource) => {
      const kind = resource.kind.toLowerCase();
      if (!acc[kind]) acc[kind] = [];
      acc[kind].push(resource);
      return acc;
    }, {} as Record<string, KubernetesResource[]>);

    Object.entries(grouped).forEach(([kind, resources]) => {
      files[`${kind}.yaml`] = resources
        .map(resource => `---\n${yaml.dump(resource, { indent: 2 })}`)
        .join('\n');
    });

    return files;
  }
};
